<template>
    <div class="user_bar">
        <language-switch class="lang-swt"/>
        <BaseDropdown :is-open="isMenuOpen" id="user-menu" @toggle="toggleMenu" style="width: unset;">
            <template #trigger>
                <div class="user-profile user-nm">
                    <span class="user" :data-tooltip="userNameTooltip"> {{ userNamePreview }} </span>
                    <i class="icon-chevron-down" :class="{ 'rotate-icon': isMenuOpen }"></i>
                </div>
            </template>

            <template #content>
                <div class="user-menu">
                    <div class="user-menu__header">
                        <span class="user-menu__name">{{ userData?.name }}</span>
                        <span class="user-menu__email">{{ userData?.email }}</span>
                    </div>
                    <div class="user-menu__content">
                        <button class="user-menu__item" @click="goToProfilePage">
                            <span>{{ $t('general.header_menu.list.profile') }}</span>
                        </button>
                        <button v-if="isMobile" class="user-menu__item" @click="exitAccount">
                            <span>{{ $t('general.header_menu.list.logout') }}</span>
                        </button>
                    </div>
                </div>
            </template>
        </BaseDropdown>

        <!-- Notifications -->
        <div class="user-notifications">
            <NotificationDropdown
                :is-open="isNotificationsOpen"
                :count="unreadNotificationsCount"
                :notifications="notifications"
                :is-loading="isLoadingNotifications"
                @toggle="handleNotificationToggle"
                @load-more="loadMoreNotifications"
            >
                <template #default="{ item }">
                    <NotificationItem
                        :item="item"
                        :description-loading-item="descriptionLoadingItem"
                        @get-description="getDescription"
                    />
                </template>
            </NotificationDropdown>
        </div>

        <div type="button" class="exit_outside" @click="exitAccount">
            <ExitIcon/>
        </div>
    </div>
</template>

<script setup>
// core
import {ref, computed, onMounted, onUnmounted} from "vue";
import {deleteCookie} from "@/helpers/cookies";
import {useStore} from "vuex";
import {useRouter} from "vue-router";

// components
import LanguageSwitch from "./LanguageSwitch.vue";
import NotificationDropdown from "./NotificationDropdown.vue";
import NotificationItem from "@/components/Notifications/NotificationItem.vue";
import ExitIcon from "@/components/Icons/ExitIcon.vue";
import BaseDropdown from "@/components/ui/BaseDropdown.vue";

const store = useStore();
const router = useRouter();

const isMobile = ref(window.innerWidth < 800);

const userData = computed(() => {
    if (!store.getters["user/getCurrentUserData"]) {
        return JSON.parse(localStorage.getItem('user-data'))
    }
    return store.getters["user/getCurrentUserData"]
});
const notifications = computed(() => store.getters["notifications/getNotifications"]);
const isLoadingNotifications = computed(() => store.getters["notifications/getIsLoading"]);
const descriptionLoadingItem = computed(() => store.getters["notifications/getDescriptionLoadingItem"]);
const unreadNotificationsCount = computed(() => store.getters['notifications/getUnreadsCount'])
const isNotificationsOpen = ref(false);
const isMenuOpen = ref(false);

const userNamePreview = computed(() => {
    const name = userData.value?.name
    if (!name) {
        return 'Profile'
    }

    return name.length > 15
        ? `${name.slice(0, 12)}...`
        : name
})

const userNameTooltip = computed(() => {
    const name = userData.value?.name
    if (!name || name.length <= 15) return null
    return name
})

const handleResize = () => {
    isMobile.value = window.innerWidth < 800;
};

const toggleMenu = (value) => {
    isMenuOpen.value = value;
};

const goToProfilePage = async () => {
    toggleMenu(false)
    await router.push({name: 'UserInfo'})
}

const checkIsAllReaded = async () => {
    if (unreadNotificationsCount.value) {
        await store.dispatch('notifications/readAllNotification')
    }
}

const handleNotificationToggle = async () => {
    isNotificationsOpen.value = !isNotificationsOpen.value;

    await checkIsAllReaded();

    if (isNotificationsOpen.value) {
        store.commit('notifications/RESET_NOTIFICATIONS');
        await loadNotifications();
    }
};

const loadNotifications = async () => {
    await store.dispatch('notifications/getNotifications');
};

const loadMoreNotifications = async () => {
    await loadNotifications();
};

const getDescription = async (notification_id) => {
    const currentNotification = notifications.value.find(n => n.id === notification_id)
    if (currentNotification?.text) return
    await store.dispatch('notifications/getNotificationDescriptionById', notification_id)
}

const logout = () => store.dispatch("auth/logout");

const exitAccount = () => {
    router.push({name: "SignIn"});
    logout();
    deleteCookie("instructed");
};

onMounted(() => {
    window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
});
</script>

<style lang="scss" scoped>
.user_bar {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: 10px;
}

::v-deep .ql-syntax {
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-wrap: break-word;
    max-width: 100%;
}

.user-profile {
    width: 137px;
    height: 55px;

    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;

    border-radius: 11px;
    background-color: var(--block-color);

    i {
        font-size: 8px;
    }

    .user {
        font-size: 14px;
        font-family: Gilroy;
        font-weight: 500;
        user-select: none;
        color: #000000ff;

        &[data-tooltip] {
            position: relative;

            &::after {
                content: attr(data-tooltip);
                position: absolute;
                bottom: 100%;
                left: 50%;
                transform: translateX(-50%);
                padding: 4px 8px;
                white-space: nowrap;
                background: #333;
                color: #fff;
                border-radius: 4px;
                opacity: 0;
                pointer-events: none;
                transition: opacity .2s;
            }

            &:hover {
                &::after {
                    opacity: 1;
                }
            }
        }
    }

    .exit_inside {
        display: none;
    }

    &:hover {
        color: var(--general-color);

        .user {
            color: var(--general-color);
        }
    }
}

.user-notifications {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 65px;
    height: 55px;
    color: #403E43;
    border-radius: 11px;
    background-color: var(--block-color);
    cursor: pointer;
}

.exit_outside {
    width: 65px;
    height: 55px;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 11px;
    background-color: var(--block-color);

    &:hover {
        cursor: pointer;
        color: var(--general-color);
    }
}

@media screen and (max-width: 800px) {
    .user-profile {
        gap: 14px;

        .exit_inside {
            display: block;
        }
    }

    .exit_outside {
        display: none;
    }
}

@media screen and (max-width: 580px) {
    .user_bar {
        flex: 1;
        justify-content: space-between;
    }
}

.user-menu {
    min-width: 200px;
    padding: 8px 0;

    &__header {
        padding: 8px 16px;
        margin-bottom: 8px;
        display: flex;
        flex-direction: column;
        border-bottom: 1px solid #d3d3d3;
        gap: 4px;
    }

    &__name {
        color: #2C2E35;
        font-size: 18px;
        font-style: normal;
        font-weight: 500;
        line-height: normal;
    }

    &__email {
        color: #131316;
        font-size: 16px;
        font-weight: 250;
        line-height: normal;
        font-style: normal;
    }

    &__content {
        display: flex;
        flex-direction: column;
    }

    &__item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 16px;
        border: none;
        background: none;
        width: 100%;
        text-align: left;
        cursor: pointer;
        color: #2C2E35;
        font-size: 14px;
        text-transform: uppercase;
        font-style: normal;
        font-weight: 300;
        line-height: normal;
        transition: background-color 0.2s;

        &:hover {
            background-color: #f5f5f5;
            color: var(--general-color);

            svg {
                color: var(--general-color);
            }
        }

        svg {
            width: 16px;
            height: 16px;
            transition: color 0.2s;
        }

    }
}

.icon-chevron-down {
    transition: transform 0.3s ease;

    &.rotate-icon {
        transform: rotate(180deg);
    }
}
</style>
